import { Typography } from '@euler/app/components/typography';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { Download, X } from 'lucide-react-native';
import { Suspense, lazy, memo, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, View } from 'react-native';
import { aiColors } from '../theme';
import { attachmentErrorText } from './attachmentError';

// The viewers weigh megabytes (a WebAssembly workbook engine, a canvas
// grid): they load on the first file opened, never with the app.
const SheetViewer = lazy(async () => await import('./SheetViewer.web'));

/** Hands the bytes to the browser as a download of the file's own name. */
function saveFile(bytes: ArrayBuffer, info: AiAttachmentInfo) {
  const url = URL.createObjectURL(new Blob([bytes], { type: info.mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = info.fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </View>
  );
}

/** A spreadsheet the user sent, full screen, to read or download. */
export const AttachmentPreview = memo(
  ({ info, onClose }: { info: AiAttachmentInfo; onClose: () => void }) => {
    const { aiAttachmentService } = useServiceFactory();
    const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
    const [failure, setFailure] = useState<string | null>(null);
    useEffect(() => {
      let active = true;
      aiAttachmentService
        .download(info.id)
        .then(async blob => await blob.arrayBuffer())
        .then(data => active && setBytes(data))
        .catch(error => active && setFailure(attachmentErrorText(error)));
      return () => {
        active = false;
      };
    }, [aiAttachmentService, info.id]);
    const spinner = (
      <Centered>
        <ActivityIndicator color={aiColors.primary} />
      </Centered>
    );
    return (
      <Modal visible transparent={false} onRequestClose={onClose}>
        <View style={{ flex: 1, backgroundColor: aiColors.card }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              paddingHorizontal: 16,
              height: 52,
              borderBottomWidth: 1,
              borderColor: aiColors.border,
            }}
          >
            <Typography.Text
              medium
              size={15}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flex: 1 }}
            >
              数据预览
            </Typography.Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="下载"
              disabled={!bytes}
              onPress={() => bytes && saveFile(bytes, info)}
              hitSlop={8}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                opacity: bytes ? 1 : 0.4,
              }}
            >
              <Download size={18} color={aiColors.primary} />
              <Typography.Text size={14} color={aiColors.primary}>
                下载
              </Typography.Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="关闭"
              onPress={onClose}
              hitSlop={8}
            >
              <X size={20} color={aiColors.mutedForeground} />
            </Pressable>
          </View>
          {failure ? (
            <Centered>
              <Typography.Text size={14} color={aiColors.mutedForeground}>
                {failure}
              </Typography.Text>
            </Centered>
          ) : bytes ? (
            <Suspense fallback={spinner}>
              <SheetViewer bytes={bytes} info={info} />
            </Suspense>
          ) : (
            spinner
          )}
        </View>
      </Modal>
    );
  },
);
